import { useClientPortalAPI, useSubmit } from "@certn/api";
import {
  Container,
  Divider,
  Link,
  showToast,
  Text,
  Toggle,
} from "@certn/cascadia-react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { PageTitle } from "../../components/page-title/PageTitle";
import { checkArgumentsTemplatePermissions } from "../../components/permissions/Permissions";
import { usePermissions } from "../../components/permissions/PermissionsProvider";
import { DisableOneIDFallbackModal } from "../../components/settings/DisableOneIDFallbackModal";
import { useUser } from "../../hooks/useUser";
import { userPermissiblePurposes } from "../../utils/user-utils";

export function CheckSettings() {
  const [showDisableFallbackModal, setShowDisableFallbackModal] =
    useState(false);
  const { t } = useTranslation();
  const {
    clientPortalQuestionnaireTemplates,
    clientPortalTruliooIdentityVerification,
    employmentVerificationsOrderable,
    clientPortalAllowDisablingSendingCompletionEmail,
    enableAddressBasedInternationalCriminalRecordCheckOrdering,
  } = useFlags();
  const { details } = useUser();
  const { hasPermission } = usePermissions();
  const { data, mutate, isLoading } = useClientPortalAPI(
    "/api/internal/client-portal/check-settings/"
  );

  const hasTenancyPermissablePurpose =
    userPermissiblePurposes(details).includes("TENANCY");
  const hasInvestingOrLendingPermissablePurpose =
    userPermissiblePurposes(details).includes("INVESTING") ||
    userPermissiblePurposes(details).includes("LENDING");
  const showEmploymentVerifications =
    employmentVerificationsOrderable &&
    hasPermission(checkArgumentsTemplatePermissions);
  const showCriminalRecordTemplates =
    enableAddressBasedInternationalCriminalRecordCheckOrdering &&
    hasPermission(checkArgumentsTemplatePermissions);
  const showQuestionnaireTemplates =
    clientPortalQuestionnaireTemplates && hasTenancyPermissablePurpose;
  const showFitnessProprietyTemplates =
    clientPortalQuestionnaireTemplates &&
    (hasTenancyPermissablePurpose || hasInvestingOrLendingPermissablePurpose);
  const hasCheckSettingEditPermission = hasPermission({
    action: "update",
    target: "check_settings",
    scope: "all",
  });

  const updateCheckSettings = useSubmit(
    "/api/internal/client-portal/check-settings/update/",
    {
      showToastOnError: true,
      onSuccess: async () => {
        showToast(t("Settings changed successfully"), { type: "success" });
        await mutate(data);
      },
      onFailure(error) {
        console.error("Error updating check settings", error);
      },
    }
  );

  const disableFallback = () => {
    updateCheckSettings.submit({
      allows_non_biometric_id_verification: false,
    });
    setShowDisableFallbackModal(false);
  };

  const toggleDisableFallback = (isSelected: boolean) => {
    if (isSelected) {
      updateCheckSettings.submit({
        allows_non_biometric_id_verification: true,
      });
    } else {
      setShowDisableFallbackModal(true);
    }
  };

  const toggleTruliooFallback = (isSelected: boolean) => {
    updateCheckSettings.submit({
      allows_trulioo_identity_verification: isSelected,
    });
  };

  const toggleCompletionNotification = (isSelected: boolean) => {
    updateCheckSettings.submit({
      send_report_complete_email: isSelected,
    });
  };

  return (
    <Container layout="full-width">
      <PageTitle>{t("Checks")}</PageTitle>
      <Text style="global/heading-medium">{t("Identity Verification")}</Text>
      <Toggle
        isSelected={data?.allows_non_biometric_id_verification ?? true}
        onChange={toggleDisableFallback}
        isDisabled={
          !hasCheckSettingEditPermission ||
          updateCheckSettings.isSubmitting ||
          isLoading
        }
        testID="enable-non-biometric-id-verification-fallback"
        description={t(
          "When enabled: Eligible* applicants who fail or opt out of OneID are automatically redirected to non-biometric ID verification. When disabled: Applicants can still opt out of OneID manually, but failed attempts won’t trigger automatic fallback."
        )}
        helpText={t(
          "*Eligibility for non-biometric ID verification requires applicants to have at least one address in Canada."
        )}
      >
        {t(
          "Allow Interac\u00AE ID verification non-biometric fallback for OneID"
        )}
      </Toggle>
      {clientPortalTruliooIdentityVerification && (
        <Toggle
          isSelected={data?.allows_trulioo_identity_verification ?? false}
          onChange={toggleTruliooFallback}
          isDisabled={
            !hasCheckSettingEditPermission ||
            updateCheckSettings.isSubmitting ||
            isLoading
          }
          testID="enable-id-validation-fallback"
          description={t(
            "Eligible applicants who fail or opt out of OneID will automatically be redirected to ID Validation if they have an address in a supported country. Interac verification can also be used instead for Canadian applicants when Interac fallback is enabled."
          )}
          helpText={t(
            "*Eligibility for non-biometric ID verification requires applicants to have an address in the supported countries."
          )}
        >
          {t("Allow ID Validation non-biometric fallback for OneID")}
        </Toggle>
      )}
      <Divider />
      {clientPortalAllowDisablingSendingCompletionEmail && (
        <>
          <Text style="global/heading-medium">{t("Notifications")}</Text>
          <Toggle
            isSelected={data?.send_report_complete_email ?? true}
            onChange={toggleCompletionNotification}
            isDisabled={
              !hasCheckSettingEditPermission ||
              updateCheckSettings.isSubmitting ||
              isLoading
            }
            testID="enable-completion-notification"
            description={t(
              "When enabled, applicants receive an email when Certn has completed processing the requested checks."
            )}
          >
            {t("Email applicants when Certn completes check processing")}
          </Toggle>
          <Divider />
        </>
      )}
      {showCriminalRecordTemplates && (
        <>
          <Text style="global/heading-medium">
            {t("Criminal Record Check")}
          </Text>
          <Text style="global/body-secondary" color="text-weak">
            <Trans>
              Manage reusable templates for International Criminal Checks with
              address-based ordering rules, residency requirements, cost
              thresholds, and Canadian Criminal Record Check preferences. Create
              and customize templates from the{" "}
              <Link
                textStyle="global/body-secondary-link"
                textColor="text-weak"
                testID="criminal-record-templates-page-link"
                href="/settings/criminal-record-templates"
              >
                Criminal Record Templates
              </Link>{" "}
              page.
            </Trans>
          </Text>
          <Divider />
        </>
      )}
      {showEmploymentVerifications && (
        <>
          <Text style="global/heading-medium">
            {t("Employment Verification")}
          </Text>
          <Text style="global/body-secondary" color="text-weak">
            <Trans>
              Certn offers two pre-set template options allowing you to choose
              between specific years of employment or number of employers.
              Custom templates can be created to match your specific
              requirements from the{" "}
              <Link
                textStyle="global/body-secondary-link"
                textColor="text-weak"
                testID="employment-verification-templates-page-link"
                href="/settings/employment-verification-templates"
              >
                Employment Verification Templates
              </Link>{" "}
              page.
            </Trans>
          </Text>
          <Divider />
        </>
      )}
      {showQuestionnaireTemplates && (
        <>
          <Text style="global/heading-medium">{t("Questionnaire")}</Text>
          <Text style="global/body-secondary" color="text-weak">
            <Trans>
              Certn offers one pre-set tenancy questionnaire template. Custom
              templates can be created to match your specific requirements from
              the{" "}
              <Link
                textStyle="global/body-secondary-link"
                textColor="text-weak"
                testID="questionnaire-templates-page-link"
                href="/settings/questionnaire-templates"
              >
                Questionnaire Templates
              </Link>{" "}
              page.
            </Trans>
          </Text>
          <Divider />
        </>
      )}
      {showFitnessProprietyTemplates && (
        <>
          <Text style="global/heading-medium">{t("Fitness & propriety")}</Text>
          <Text style="global/body-secondary" color="text-weak">
            <Trans>
              View the standard fitness and propriety declaration template on
              the{" "}
              <Link
                textStyle="global/body-secondary-link"
                textColor="text-weak"
                testID="fitness-propriety-templates-page-link"
                href="/settings/fitness-propriety-templates"
              >
                Fitness &amp; propriety templates
              </Link>{" "}
              page.
            </Trans>
          </Text>
          <Divider />
        </>
      )}
      <DisableOneIDFallbackModal
        show={showDisableFallbackModal}
        onClose={() => setShowDisableFallbackModal(false)}
        disableFallback={disableFallback}
      />
    </Container>
  );
}
